'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import CaseCard from '@/components/platform/v2/CaseCard'
import { BUSINESS_TYPES, CASES, CASE_TASKS } from '@/data/cases'
import { cn } from '@/lib/utils'
import { PageCta, PageHero } from '@/components/platform/v2/PageEdge'

const Chip = ({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
            'rounded-full border px-4 py-2 text-xs sm:text-sm font-semibold transition-colors',
            active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border/70 bg-[hsl(var(--charcoal))] text-foreground/80 hover:border-foreground/40',
        )}
    >
        {children}
    </button>
)

const Cases = () => {
    const [type, setType] = useState<string>('Все')
    const [task, setTask] = useState<string | null>(null)

    const filtered = useMemo(
        () => CASES.filter((c) => (type === 'Все' || c.businessType === type) && (!task || c.tasks.includes(task as never))),
        [type, task],
    )

    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <PageHero
                eyebrow="Кейсы VCENTRE.ONE"
                title={
                    <>
                        Разные компании.
                        <br />
                        Разные процессы.
                        <br />
                        Одна платформа.
                    </>
                }
                text={
                    <p>
                        Посмотрите, как VCENTRE.ONE превращается в приложение, CRM, сайт, клиентский кабинет, систему продаж или целую
                        цифровую среду в зависимости от задачи бизнеса.
                    </p>
                }
                actions={
                    <>
                        <Link href="/signin?source=cases" className="btn-primary">
                            Собрать похожий сценарий для моего бизнеса
                        </Link>
                        <Link href="/platform" className="btn-ghost">
                            Возможности платформы
                        </Link>
                    </>
                }
                visual={
                    <img
                        src={'/assets/images/platform/cases-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT с ноутбуком и фразой «Идеи в действии»"
                        width={768}
                        height={758}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            <Section tone="quiet" className="pt-8">
                <div className="surface-quiet flex flex-col gap-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                            Тип бизнеса
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {BUSINESS_TYPES.map((t) => (
                                <Chip key={t} active={type === t} onClick={() => setType(t)}>
                                    {t}
                                </Chip>
                            ))}
                        </div>
                    </div>
                    <div className="hairline" />
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Задача</span>
                        <div className="flex flex-wrap gap-2">
                            {CASE_TASKS.map((t) => (
                                <Chip key={t} active={task === t} onClick={() => setTask(task === t ? null : t)}>
                                    {t}
                                </Chip>
                            ))}
                        </div>
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="surface mt-10 text-center">
                        <p className="display-3">Под такой набор фильтров кейса пока нет</p>
                        <p className="body-text mx-auto mt-3 max-w-xl">
                            Расскажите про свой процесс — покажем, как он собирается в VCENTRE.ONE.
                        </p>
                        <Link href="/signin?source=cases" className="btn-primary mt-6">
                            Собрать похожий сценарий для моего бизнеса
                        </Link>
                    </div>
                ) : (
                    <div className="mt-10 grid gap-5 lg:grid-cols-2">
                        {filtered.map((c, i) => (
                            <CaseCard
                                key={c.slug}
                                item={c}
                                size={i === 0 && filtered.length > 2 ? 'flagship' : 'medium'}
                                className={i === 0 && filtered.length > 2 ? 'lg:col-span-2' : ''}
                            />
                        ))}
                    </div>
                )}
            </Section>

            <Section>
                <PageCta
                    title="Ваш бизнес работает по-другому? Так и должно быть."
                    text="Расскажите нам о процессе — мы покажем, как его можно собрать в VCENTRE.ONE."
                    visual={
                        <img
                            src={'/assets/images/platform/cases-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем быстро»"
                            width={756}
                            height={760}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                    actions={
                        <>
                            <Link href="/signin?source=cases" className="btn-primary">
                                Собрать похожий сценарий для моего бизнеса
                            </Link>
                            <Link href="/platform" className="btn-ghost">
                                Возможности платформы
                            </Link>
                        </>
                    }
                />
            </Section>
        </div>
    )
}

export default Cases
